import { useState } from "react";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";

import { api } from "~/utils/api";

import { Label } from "~/components/v3/shadcn/Label";
import { Button } from "~/components/v3/themed/Button";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "~/components/v3/shadcn/InputOTP";
import { Card, CardFooter, CardContent } from "~/components/v3/shadcn/Card";

type AddPhoneNumberModalProps = {
    setShowModal: (show: boolean) => void;
    refetchCallback?: () => void;
    verifiedCallback?: () => void;
};

export const VerifyPhoneNumber = (props: AddPhoneNumberModalProps) => {
    const { data: session, update: updateSession } = useSession();

    const [verificationCode, setVerificationCode] = useState<string>("");
    const verifyPhoneNumber = api.user.verifyPhoneNumber.useMutation();
    const [error, setError] = useState<string>("");

    const handleVerifyPhoneNumber = async () => {
        if (!/^\d{6}$/.test(verificationCode)) {
            toast.error("Please enter a valid 6-digit verification code.");
            return;
        }

        const toastId = toast.loading("Verifying...");

        await verifyPhoneNumber.mutateAsync(
            { verificationCode },
            {
                onSuccess: async (isVerified) => {
                    if (!isVerified) {
                        setError(
                            "Incorrect Verification code. Please try again",
                        );

                        toast.update(toastId, {
                            render: "Incorrect Verification code. Please try again",
                            type: "error",
                            isLoading: false,
                            autoClose: 1300,
                        });

                        return;
                    }

                    await updateSession({
                        ...session,
                        user: {
                            ...session?.user,
                            isUserPhoneNumberVerified: true,
                        },
                    });
                    toast.update(toastId, {
                        render: "Phone number verified!",
                        type: "success",
                        isLoading: false,
                        autoClose: 1300,
                    });
                    props.setShowModal(false);
                    if (props.refetchCallback) props.refetchCallback();
                    if (props.verifiedCallback) props.verifiedCallback();
                },
                onError: () => {
                    toast.update(toastId, {
                        render: "Verification failed. Please try again.",
                        type: "error",
                        isLoading: false,
                        autoClose: 1300,
                    });
                },
            },
        );
    };

    return (
        <Card className="flex w-auto max-w-md flex-col gap-8 px-0 py-0">
            <CardContent className="mt-6 p-0">
                <div className="flex flex-col gap-4">
                    <div className="flex w-full flex-col gap-2">
                        <Label htmlFor="otp-input">
                            Enter the 6-digit code
                        </Label>
                        <InputOTP
                            value={verificationCode}
                            onChange={setVerificationCode}
                            maxLength={6}
                        >
                            <InputOTPGroup>
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <InputOTPSlot key={index} index={index} />
                                ))}
                            </InputOTPGroup>
                        </InputOTP>

                        {error && <p className="text-red-500">{error}</p>}
                    </div>
                </div>
            </CardContent>

            <CardFooter className="flex justify-between p-0">
                <Button
                    variant="secondary"
                    onClick={() => props.setShowModal(false)}
                >
                    Go back
                </Button>
                <Button onClick={handleVerifyPhoneNumber}>Confirm</Button>
            </CardFooter>
        </Card>
    );
};
